import React, { ChangeEvent, FC, memo } from 'react';
import { ReactComponent as InfoIcon } from 'applicaiton/assets/info.svg';
import { UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  StyledInput,
  TextCounter,
  LabelWrapper,
  TextAreaWrapper,
} from './styled';

interface TextAreaProps {
  id: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  errorMessage?: string;
  hint?: string;
  register?: UseFormRegister<any>;
  isFocused?: boolean;
  isCount?: boolean;
  textLength?: string;
  maxTextLength?: number;
  onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  value?: string;
  showRequiredIcon?: boolean;
}

/**
 * A memoized React functional component that renders a styled textarea input field with
 * optional label, placeholder, and hint text. It also supports form validation using the
 * react-hook-form library.
 *
 * @param {string} id - The unique identifier for the textarea element.
 * @param {string} [placeholder] - Optional placeholder text to display within the textarea.
 * @param {string} [label] - Optional label text to display above the textarea.
 * @param {boolean} [disabled=false] - Whether the textarea should be disabled and uneditable.
 * @param {string} [errorMessage] - Optional error message to display if validation fails.
 * @param {string} [hint] - Optional hint text to display below the textarea.
 * @param {UseFormRegister<any>} [register] - A function from the react-hook-form library that
 * registers the input field with the form, enabling validation and submission handling.
 * @param {boolean} [isFocused=false] - Whether the textarea should be automatically focused
 * when rendered.
 * @param {boolean} [isCount=false] - Whether to display a character count below the textarea.
 * @param {string} [textLength] - Optional string representing the current length of the text in
 * the textarea. Used with isCount to display the count.
 * @param {number} [maxTextLength] - Optional maximum allowed length of the text in the
 * textarea, used for validation and character counting.
 * @param {(e: ChangeEvent<HTMLTextAreaElement>) => void} [onChange] - Optional callback function
 * that will be called whenever the value of the textarea changes.
 * @param {string} [value] - The current value of the textarea. Used in controlled components
 * to synchronize the input field's state with external data.
 * @param {boolean} [showRequiredIcon=false] - Whether to display a required icon next to the label.
 *
 * @returns {JSX.Element} - A React element that renders the styled textarea input field.
 */
const TextArea: FC<TextAreaProps> = ({
  id,
  placeholder,
  label,
  disabled,
  errorMessage,
  hint,
  register,
  isFocused,
  isCount = true,
  textLength = '',
  maxTextLength = 2000,
  value,
  onChange,
  showRequiredIcon,
}) => {
  const { t } = useTranslation();

  return (
    <StyledInput isError={!!errorMessage} disabled={disabled}>
      <LabelWrapper>
        <label>{label}</label>
        {showRequiredIcon && <InfoIcon />}
      </LabelWrapper>
      <TextAreaWrapper disabled={disabled}>
        {register ? (
          <textarea
            placeholder={placeholder}
            disabled={disabled}
            {...register(id)}
            autoFocus={isFocused}
            rows={7}
            maxLength={maxTextLength}
          />
        ) : (
          <textarea
            placeholder={placeholder}
            disabled={disabled}
            id={id}
            autoFocus={isFocused}
            rows={7}
            maxLength={maxTextLength}
            value={value}
            onChange={onChange}
          />
        )}
      </TextAreaWrapper>
      {isCount && (
        <TextCounter>
          {maxTextLength - textLength.length}{' '}
          {textLength.length === 1
            ? t('editProfileSettings.oneCharacterLeft')
            : t('editProfileSettings.charactersLeft')}
        </TextCounter>
      )}
      <span>{errorMessage ? errorMessage : hint}</span>
    </StyledInput>
  );
};

export default memo(TextArea);
